<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    :nudge-width="500"
    :min-width="computedMinWidth"
    max-width="1400px"
    max-height="800px"
    offset-y
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn color="success" depressed v-bind="attrs" v-on="on">
        <v-icon left>mdi-plus</v-icon>Hinzufügen
      </v-btn>
    </template>

    <v-card outlined>
      <v-card-title class="font-weight-medium"
        ><span class="text-subtitle-1 font-weight-medium">{{ title }}</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="close()"
          ><v-icon small>mdi-close</v-icon></v-btn
        >
      </v-card-title>
      <v-card-subtitle>
        <v-text-field
          v-model="search"
          outlined
          dense
          label="Suchen"
          hide-details="auto"
          prepend-inner-icon="mdi-magnify"
          class="mt-2"
          clearable
        ></v-text-field>
        <v-select
          v-if="showStatusFilter"
          v-model="statusFilter"
          :items="statusValues"
          label="Nach Status filtern"
          outlined
          multiple
          dense
          required
          item-text="text"
          item-value="value"
          prepend-inner-icon="mdi-filter"
          hide-details="auto"
          class="mt-3"
        >
          <template v-slot:selection="data">
            <personnel-record-status-chip
              :status="data.item.value"
              small
              class="mr-1"
            >
            </personnel-record-status-chip>
          </template>
        </v-select>
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-data-table
        v-model="selectionLocal"
        :headers="headers"
        :items="listItems"
        :search="search"
        item-key="user.uid"
        sort-by="lastName"
        group-by="groupByDivision"
        fixed-header
        height="300"
        dense
        show-select
        checkbox-color="primary"
        mobile-breakpoint="0"
        disable-pagination
        hide-default-footer
      >
        <!-- Forward all slots to the data table with fallbacks -->
        <template v-for="(_, slot) in $scopedSlots" v-slot:[slot]="scope">
          <slot :name="slot" v-bind="scope" />
        </template>

        <!-- Default templates - only render if no slot provided -->
        <template
          v-if="!$scopedSlots['group.header']"
          v-slot:[`group.header`]="{ items, isOpen, toggle }"
        >
          <th :colspan="headers.length + 1">
            <v-icon @click="toggle" class="mr-2"
              >{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
            <span>
              {{ stripPrefix(items[0].groupByDivision) }}
            </span>
          </th>
        </template>

        <template
          v-if="showPersonnelNumber && !$scopedSlots['item.personnelNumber']"
          v-slot:[`item.personnelNumber`]="{ item }"
        >
          <pre>{{ item.personnelNumber }}</pre>
        </template>

        <template
          v-if="showStatusFilter && !$scopedSlots['item.status']"
          v-slot:[`item.status`]="{ item }"
        >
          <personnel-record-status-chip :status="item.status" small>
          </personnel-record-status-chip>
        </template>
      </v-data-table>
      <v-divider></v-divider>
      <v-card-text>
        <slot name="additionalForm"></slot>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="px-3">
        <v-btn depressed
          ><v-icon left @click="$emit('resetForm')">mdi-reload</v-icon
          >Zurücksetzen</v-btn
        >
        <v-spacer></v-spacer>
        <v-btn
          depressed
          color="success"
          @click="submitForm()"
          :disabled="addBtnDisabled"
          ><v-icon left>mdi-plus</v-icon>Hinzufügen</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<style></style>

<script>
import PersonnelRecordStatusChip from "@/components/member-management/personnel-records/PersonnelRecordStatusChip.vue";
export default {
  name: "new-entry-table-selector-menu",
  props: {
    selection: {},
    items: {},
    title: {
      type: String,
      default: "Personen auswählen",
      required: false,
    },
    additionalHeaders: {
      type: Array,
      default: () => [],
    },
    addBtnDisabled: {
      type: Boolean,
      default: false,
    },
    dataType: {
      type: String,
      default: "personnelRecord",
      validator: (value) => ["personnelRecord", "membership"].includes(value),
    },
    showPersonnelNumber: {
      type: Boolean,
      default: true,
    },
    showStatusFilter: {
      type: Boolean,
      default: true,
    },
  },
  model: {
    prop: "selection",
    event: "selectionChange",
  },
  components: { PersonnelRecordStatusChip },
  computed: {
    selectionLocal: {
      get: function () {
        return this.selection;
      },
      set: function (value) {
        this.$emit("selectionChange", value);
      },
    },
    computedMinWidth() {
      // If both personnelNumber and statusFilter are disabled, use smaller width
      if (!this.showPersonnelNumber && !this.showStatusFilter) {
        return "550px";
      }
      // Default width for full functionality
      return "850px";
    },
    listItems() {
      return this.items
        .map((record) => {
          // Get division for grouping without modifying the original record
          const division = this.getDivision(record);
          return {
            ...record,
            groupByDivision:
              division.sortKey.toString().padStart(4, "0") +
              "_" +
              division.title,
          };
        })
        .filter((record) => {
          // Only apply status filter if showStatusFilter is enabled
          if (!this.showStatusFilter) {
            return true; // Show all records when status filter is disabled
          }
          const status = this.getStatus(record);
          return this.statusFilter.includes(status);
        });
    },
    defaultHeaders() {
      return this.headers.filter((header) => header.default);
    },
    defaultState() {
      return (
        this.selectionLocal.length === this.defaultHeaders.length &&
        this.selectionLocal.every((item) => item.default)
      );
    },
    headers() {
      const personnelNumberPath =
        this.dataType === "membership"
          ? "user.personnelNumber"
          : "personnelNumber";
      const firstNamePath =
        this.dataType === "membership" ? "user.firstName" : "firstName";
      const lastNamePath =
        this.dataType === "membership" ? "user.lastName" : "lastName";

      let headers = [];

      // Conditionally add personnelNumber column
      if (this.showPersonnelNumber) {
        headers.push({
          text: "Personalnr.",
          value: personnelNumberPath,
          align: "start",
        });
      }

      // Always include name columns
      headers.push({
        text: "Vorname",
        value: firstNamePath,
        align: "start",
      });

      headers.push({
        text: "Nachname",
        value: lastNamePath,
        align: "start",
        divider: !this.showStatusFilter, // Only add divider if status column won't be shown
      });

      // Conditionally add status column
      if (this.showStatusFilter) {
        headers.push({
          text: "Status",
          value: "status",
          align: "start",
          divider: true,
        });
      }

      return headers.concat(this.additionalHeaders);
    },
  },
  data() {
    return {
      menu: false,
      search: "",
      statusFilter: ["active", "onLeave", "transfer"],
      statusValues: [
        { text: "Aktiv", value: "active", color: "#4CAF50" },
        { text: "Beurlaubt", value: "onLeave", color: "#FF9800" },
        { text: "Ausgetreten", value: "resigned", color: "#795548" },
        { text: "Im Übertritt", value: "transfer", color: "#CDDC39" },
        { text: "Entlassen", value: "dismissed", color: "#F44336" },
        { text: "Verstorben", value: "deceased", color: "#9E9E9E" },
      ],
    };
  },
  created() {
    // Remove the problematic selectionLocalBuffer assignment
  },
  methods: {
    close() {
      this.menu = false;
    },
    submitForm() {
      this.$emit("submitForm", this.selectionLocal);
      this.close();
    },
    stripPrefix(item) {
      return item.substring(item.indexOf("_") + 1);
    },
    getDivision(record) {
      if (this.dataType === "membership") {
        return record.user.division; // Division is at top level for membership
      } else {
        return record.division;
      }
    },
    getStatus(record) {
      return record.status; // Status is at top level for both types
    },
  },
};
</script>
